import { createChatMessage } from '../../utils/chat-utils.js';
import { normalizeShops } from '../../services/shop-normalizer.js';
import type { ButtonOption, ChatMessage, Coupon, ImageItem, Recipe, Shop } from './types.js';

type RenderableBlock = {
  hierarchy_buttons?: ButtonOption[];
  normal_buttons?: ButtonOption[];
  list_buttons?: ButtonOption[];
  csat?: ButtonOption[];
  recipes?: Recipe[];
  coupons?: Coupon[];
  shops?: Shop[];
  image?: ImageItem;
};

function toObject(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  return value as Record<string, unknown>;
}

function toArray<T>(value: unknown): T[] | undefined {
  return Array.isArray(value) ? (value as T[]) : undefined;
}

function toImage(value: unknown): ImageItem | undefined {
  const obj = toObject(value);
  if (!obj) return undefined;

  const sourceFromSource = typeof obj.source === 'string' ? obj.source : undefined;
  const sourceFromUrl = typeof obj.url === 'string' ? obj.url : undefined;
  const source = sourceFromSource || sourceFromUrl;
  if (!source) return undefined;

  return {
    source,
    link: typeof obj.link === 'string' || obj.link === null ? (obj.link as string | null) : undefined,
  };
}

function toCsat(value: unknown): ButtonOption[] | undefined {
  if (Array.isArray(value)) return value as ButtonOption[];

  const obj = toObject(value);
  if (!obj) return undefined;

  if (obj.type === 'rating' && Array.isArray(obj.choices)) {
    return (obj.choices as unknown[])
      .filter((choice): choice is number => typeof choice === 'number')
      .map((choice) => ({ label: String(choice), value: String(choice) }));
  }

  return undefined;
}

function hasRenderableData(msg: ChatMessage): boolean {
  return (
    !!msg.hierarchy_buttons?.length ||
    !!msg.normal_buttons?.length ||
    !!msg.list_buttons?.length ||
    !!msg.csat?.length ||
    !!msg.recipes?.length ||
    !!msg.coupons?.length ||
    !!msg.shops?.length ||
    !!msg.image
  );
}

function buildBlockMessage(block: RenderableBlock): ChatMessage | null {
  const msg = createChatMessage('', 'assistant', 'incoming');
  msg.hierarchy_buttons = block.hierarchy_buttons;
  msg.normal_buttons = block.normal_buttons;
  msg.list_buttons = block.list_buttons;
  msg.csat = block.csat;
  msg.recipes = block.recipes;
  msg.coupons = block.coupons;
  msg.shops = block.shops;
  msg.image = block.image;
  return hasRenderableData(msg) ? msg : null;
}

function normalizeReconnectExtraBlocks(raw: unknown): RenderableBlock[] {
  if (Array.isArray(raw)) {
    return raw
      .map((item) => toRenderableBlock(item))
      .filter((item): item is RenderableBlock => !!item);
  }

  const root = toObject(raw);
  if (!root) return [];

  if (Array.isArray(root.data)) {
    return root.data
      .map((item) => toRenderableBlock(item))
      .filter((item): item is RenderableBlock => !!item);
  }

  if (root.data && typeof root.data === 'object') {
    const mapped = toRenderableBlock(root.data);
    return mapped ? [mapped] : [];
  }

  const mapped = toRenderableBlock(root);
  return mapped ? [mapped] : [];
}

function toRenderableBlock(raw: unknown): RenderableBlock | null {
  const obj = toObject(raw);
  if (!obj) return null;

  return {
    hierarchy_buttons: toArray<ButtonOption>(obj.hierarchy_buttons),
    normal_buttons: toArray<ButtonOption>(obj.normal_buttons),
    list_buttons: toArray<ButtonOption>(obj.list_buttons),
    csat: toCsat(obj.csat),
    recipes: toArray<Recipe>(obj.recipes),
    coupons: toArray<Coupon>(obj.coupons),
    // Reconnect history keeps the block key the backend actually sends: "stores"
    // (there is no "shops" key anywhere in the BFF payloads).
    shops: normalizeShops(obj.stores),
    image: toImage(obj.image),
  };
}

export function buildAssistantMessagesFromLivePayload(payload: unknown): ChatMessage[] {
  const data = toObject(payload);
  if (!data) return [];

  const messages: ChatMessage[] = [];
  const text = typeof data.data === 'string' ? data.data : '';
  if (text) {
    messages.push(createChatMessage(text, 'assistant', 'incoming'));
  }

  const block = buildBlockMessage({
    hierarchy_buttons: toArray<ButtonOption>(data.hierarchy_buttons),
    normal_buttons: toArray<ButtonOption>(data.normal_buttons),
    list_buttons: toArray<ButtonOption>(data.list_buttons),
    csat: toCsat(data.csat),
    recipes: toArray<Recipe>(data.recipes),
    coupons: toArray<Coupon>(data.coupons),
    shops: toArray<Shop>(data.shops),
    image: toImage(data.image),
  });

  if (block) messages.push(block);
  return messages;
}

export function buildAssistantMessagesFromReconnect(
  responseText: string,
  responseExtra: unknown
): ChatMessage[] {
  const assistantMessages: ChatMessage[] = [];

  if (responseText) {
    assistantMessages.push(createChatMessage(responseText, 'assistant', 'incoming'));
  }

  const blocks = normalizeReconnectExtraBlocks(responseExtra);
  for (const block of blocks) {
    const mapped = buildBlockMessage(block);
    if (mapped) assistantMessages.push(mapped);
  }

  return assistantMessages;
}